// frontend/admin-portal/src/pages/Suppliers.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Badge,
  Alert,
} from "@fiberhome/shared-ui";
import { supplierService } from "../services/supplierService";
import SupplierStats from "../components/suppliers/SupplierStats";
import SupplierFilters from "../components/suppliers/SupplierFilters";
import SupplierModal from "../components/suppliers/SupplierModal";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

export default function Suppliers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    country: "",
    city: "",
    isActive: "",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      }
      const { items, pagination } = await supplierService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const handleEdit = (s) => {
    setEditing(s);
    setModalOpen(true);
  };
  const handleSaved = () => {
    setRefreshKey((k) => k + 1);
    load();
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await supplierService.remove(s.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Supplier",
      render: (r) => (
        <div
          onClick={() => navigate(`/suppliers/${r.id}`)}
          style={{ cursor: "pointer" }}
        >
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {r.code} {r.contactName ? `· ${r.contactName}` : ""}
          </div>
        </div>
      ),
    },
    {
      key: "contact",
      label: "Contact",
      render: (r) => (
        <div style={{ fontSize: 13 }}>
          {r.phone && <div>{r.phone}</div>}
          {r.email && <div className="text-muted">{r.email}</div>}
          {!r.phone && !r.email && <span className="text-muted">—</span>}
        </div>
      ),
    },
    {
      key: "location",
      label: "Location",
      render: (r) =>
        [r.city, r.country].filter(Boolean).join(", ") || (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: "productCount",
      label: "Products",
      render: (r) => <strong>{r.productCount ?? 0}</strong>,
    },
    {
      key: "paymentTermsDays",
      label: "Terms",
      render: (r) => `${r.paymentTermsDays || 0} days`,
    },
    {
      key: "isActive",
      label: "Status",
      render: (r) => (
        <Badge variant={r.isActive ? "success" : "default"}>
          {r.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 220,
      render: (r) => (
        <div className="d-flex gap-1">
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate(`/suppliers/${r.id}`)}
          >
            View
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleEdit(r)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleDelete(r)}
            disabled={r.productCount > 0}
            title={r.productCount > 0 ? "Unlink products first" : "Delete"}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Suppliers"
        subtitle="Manufacturers, distributors, and vendors"
        breadcrumbs={["Home", "Suppliers"]}
        actions={
          <Button variant="primary" onClick={handleNew}>
            + New Supplier
          </Button>
        }
      />

      <SupplierStats refreshKey={refreshKey} />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <SupplierFilters filters={filters} onChange={setFilters} />
      </Card>

      <Card
        title={`Suppliers (${pagination.total})`}
        actions={
          <div className="fh-products-toolbar">
            <div className="fh-products-toolbar__per-page">
              <span className="text-muted" style={{ fontSize: 12 }}>
                Per page
              </span>
              <select
                className="fh-input fh-input--compact"
                value={filters.limit}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    limit: Number(e.target.value),
                    page: 1,
                  }))
                }
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="fh-products-toolbar__pager">
              <Button
                size="sm"
                variant="ghost"
                disabled={pagination.page <= 1}
                onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
              >
                Prev
              </Button>
              <span className="text-muted" style={{ fontSize: 12 }}>
                Page {pagination.page} / {pagination.pages || 1}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        }
      >
        <Table
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="No suppliers match your filters"
        />
      </Card>

      <SupplierModal
        open={modalOpen}
        supplier={editing}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </>
  );
}
